import React from 'react';
import propTypes from 'prop-types';
import { Form } from 'shared-ui';
import { isDefined, isValidDate, ObjectAccessor, formatDate, isBoolean } from '@dateam/ark';
import { concatClassName } from '@dateam/ark-react';
import { plotDetailsPropTypes } from 'data/plot';
import { validate as validator, ValidationError } from 'data/observation';
import { ObservationProps } from './types';
import { ObservationErrors } from './components';
import ObservationField from './ObservationField';
import ObservationItem from './ObservationItem';
import styles from './Observation.module.scss';

const observationKey = 'observation34';

type Props = ObservationProps<App.Observation34>;

const getPresenceLabel = (presence: boolean | null) => {
    if (isBoolean(presence)) return presence ? 'Oui' : 'Non';

    return '-';
};

const Observation34: React.FC<Props> = ({
    data,
    plot,
    ignorable,
    className,
    onChange
}: Props) => {
    const [validationErrors, setValidationErrors] = React.useState<Record<string, ValidationError>>({});
    const internalData = React.useRef(data);

    React.useEffect(() => {
        internalData.current = data;
    }, [data]);

    React.useEffect(() => {
        if (data.presence === undefined &&
            data.count === undefined) return;

        setValidationErrors(validator(data, plot));
    }, [plot, data]);

    const presence = React.useMemo(() => {
        if (data.ignored) return undefined;

        return data.presence ?? undefined;
    }, [data]);

    const count = React.useMemo(() => {
        if (data.ignored) return undefined;

        return data.count ?? undefined;
    }, [data]);

    const previousValue = React.useMemo(() => {
        if (isDefined(internalData.current.previous)) {
            return (
                <>
                    {isValidDate(internalData.current.previous.updatedOn) && (
                        <div className={styles['previousDate']}>{formatDate(internalData.current.previous.updatedOn)}</div>
                    )}
                    <div className={styles['previous']}>Oui/Non: {getPresenceLabel(internalData.current.previous.presence?.value ?? null)}</div>
                    <div className={styles['previous']}>Comptage: {internalData.current.previous.count?.value ?? '-'}</div>
                </>
            );
        }

        return (<div>Aucune valeur disponible</div>);
    }, [internalData.current]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleIgnoreObservation = React.useCallback(() => {
        const newData: App.Observation34 = {
            ...internalData.current,
            presence: null,
            count: null,
            ignored: !internalData.current.ignored
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    const handlePresenceChange = React.useCallback((newValue: boolean | undefined) => {
        const newData: App.Observation34 = {
            ...internalData.current,
            presence: newValue ?? null
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    const handleCountChange = React.useCallback((newValue: number | undefined) => {
        const newData: App.Observation34 = {
            ...internalData.current,
            count: newValue ?? null
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    const handleFocus = React.useCallback((field: ObjectAccessor<Omit<App.Observation34, 'previous'>>) => {
        if (internalData.current[field] !== undefined) return;

        const newData: App.Observation34 = {
            ...internalData.current,
            [field]: null
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    return (
        <ObservationItem
            data={data}
            ignorable={ignorable}
            onIgnore={handleIgnoreObservation}
            previous={previousValue}
            label="Cicadelle Flavescence Dorée"
        >
            <div className="row">
                <ObservationField
                    id={`${observationKey}-presence`}
                    label="Oui/Non"
                    className={concatClassName(styles[`${observationKey}-presence`], 'col-4')}
                >
                    <Form.Select
                        selected={presence}
                        onChange={handlePresenceChange}
                        state={validationErrors['presence']?.type}
                        onFocus={() => handleFocus('presence')}
                        required={!data.ignored}
                        readOnly={data.ignored}
                    >
                        <Form.Select.Option value={null} />
                        <Form.Select.Option value={true}>Oui</Form.Select.Option>
                        <Form.Select.Option value={false}>Non</Form.Select.Option>
                    </Form.Select>
                </ObservationField>
                <ObservationField
                    id={`${observationKey}-count`}
                    label="Comptage"
                    offLabel="/50 feuilles"
                    className={concatClassName(styles[`${observationKey}-count`], 'col-4')}
                >
                    <Form.Number
                        min={0}
                        max={50}
                        value={count}
                        onChange={handleCountChange}
                        state={validationErrors['count']?.type}
                        readOnly={data.ignored}
                    />
                </ObservationField>
            </div>
            <ObservationErrors value={validationErrors} observationKey={observationKey} />
        </ObservationItem>
    );
};

Observation34.propTypes = {
    data: propTypes.any.isRequired,
    plot: plotDetailsPropTypes.isRequired,
    className: propTypes.string,
    onChange: propTypes.func
};

Observation34.defaultProps = {
    className: undefined,
    onChange: undefined
};

export default Observation34;
